import { computed, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { rouletteApi } from '@/api'
import type { SpinRead } from '@/api'
import { POLLING_INTERVALS, STALE_TIME, RETRY_CONFIG } from '@/config/query'
import { assertPersonalSession, personalSession, requirePersonalSession } from '../personal-session'
import { normalizeTonAddress } from '@/utils/ton-address'

// UI type for spin records
export interface SpinRecord {
  id: string
  date: string
  time: string
  result: 'win' | 'miss'
  amount?: number
  tx?: string
}

interface UseSpinHistoryOptions {
  enabled?: Ref<boolean | undefined>
  limit?: number
}

function transformSpinData(spins: SpinRead[]): SpinRecord[] {
  return spins.map((spin) => {
    const date = new Date(spin.created_at)
    const prize = spin.prizes[0]
    return {
      id: spin.id,
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
      result: spin.won ? 'win' : 'miss',
      amount: prize?.amount ?? undefined,
      tx: prize?.explorer_link ?? undefined,
    }
  })
}

export function useSpinHistory(options: UseSpinHistoryOptions = {}) {
  const { enabled, limit = 10 } = options
  const queryEnabled = computed(() => Boolean(personalSession.value) && (enabled?.value ?? true))

  const { data, error, isFetching, refetch } = useQuery({
    queryKey: computed(() => ['roulette', 'history', personalSession.value?.id ?? null, limit]),
    queryFn: async ({ queryKey }) => {
      const session = requirePersonalSession()
      if (queryKey[2] !== session.id) throw new Error('Wallet session changed.')
      const response = await rouletteApi.getUserSpins(limit, 0)
      assertPersonalSession(session)
      if (response.success && response.data) {
        if (
          response.data.items.some(
            (spin) => normalizeTonAddress(spin.user.wallet) !== session.wallet
          )
        ) {
          throw new Error('Spin history belongs to another wallet. Please reconnect.')
        }
        return { sessionId: session.id, value: response.data }
      }
      throw new Error(response.error ?? 'Failed to fetch spin history')
    },
    refetchInterval: POLLING_INTERVALS.BACKEND_API,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: RETRY_CONFIG.ATTEMPTS,
    staleTime: STALE_TIME.MEDIUM,
    enabled: queryEnabled,
  })

  return {
    spinHistory: computed(() => {
      if (!queryEnabled.value || !data.value || data.value.sessionId !== personalSession.value?.id)
        return []
      return transformSpinData(data.value.value.items)
    }),
    pagination: computed(() => {
      if (!queryEnabled.value || !data.value || data.value.sessionId !== personalSession.value?.id)
        return null
      return {
        total: data.value.value.total,
        limit: data.value.value.limit,
        offset: data.value.value.offset,
      }
    }),
    isLoading: computed(() => isFetching.value && !data.value),
    hasError: computed(() => Boolean(error.value)),
    errorMessage: computed(() => error.value?.message ?? 'Failed to load spin history'),
    refetch: async () => {
      if (queryEnabled.value) return refetch()
    },
  }
}
